import { pickFolderAssignment } from "routes/skill-mapper/server/schedule-assignment-routing.mjs"

/**
 * Selection on the schedule assignment board (FIQ-2457). Ticking a folder header selects
 * the folder itself, so assigning it creates one folder assignment that later schedules
 * in that library folder follow.
 *
 * A selected folder covers every listed schedule filed under it anywhere in the library
 * (a schedule can be filed in several folders, and under a folder's subfolders), because
 * those are the schedules its folder assignment will route: they show ticked and count.
 *
 * While a search is active the user has asked to see a subset, so a folder tick only
 * selects the schedules on screen, and a folder ticked before the search stands for only
 * the schedules on screen too. Every unassigned schedule that gets assigned is counted.
 * Unticking while searching (a row, a folder or Select all) changes only what is on screen:
 * the hidden schedules a folder tick covered stay ticked for when the search is cleared.
 *
 * A folder assignment can also take over schedules already routed by another folder's
 * assignment, when they are filed in both and the new folder wins (deeper, or first in the
 * library). Those are not in the list, so they are not counted as selected; folderTakeovers
 * finds them so the selection bar can say they will move too.
 *
 * Listed schedules carry folderIds: every folder they are filed under (see buildAssignmentBoard).
 * Selections are immutable: every helper returns a new { folders, schedules } object.
 */

export const EMPTY_SELECTION = { folders: new Set(), schedules: new Set() }

/**
 * The schedules already routed by another folder's assignment that assigning these targets'
 * folders would move: they are filed under one of the new folders too, and that folder wins
 * (see pickFolderAssignment). Schedule and task targets never take over a folder's work
 * beyond themselves, so only folder targets count.
 *
 * @param {{ folderRouted: object[], folderAssignments: Map }} board - From buildAssignmentBoard
 * @param {Array<{ kind: string, targetId: string }>} targets - As selectionTargets returns them
 * @returns {object[]} The schedules' folderRouted entries
 */
export function folderTakeovers(board, targets) {
    const newFolders = new Set(targets.filter(isFolderTarget).map(toTargetId))
    if (!newFolders.size || !board.folderRouted?.length) return []
    return board.folderRouted.filter(isTakenOver)

    function isTakenOver({ placements }) {
        return pickFolderAssignment(placements, assignmentAfter) === NEW_FOLDER
    }

    function assignmentAfter(folderId) {
        return newFolders.has(folderId) ? NEW_FOLDER : board.folderAssignments.get(folderId)
    }
}

const NEW_FOLDER = { kind: "folder", pendingTakeover: true }

function isFolderTarget(target) {
    return target.kind === "folder"
}

function toTargetId(target) {
    return String(target.targetId)
}

/**
 * @param {object} selection
 * @param {{ scheduleId: string, folderIds?: string[] }} schedule - A listed schedule
 */
export function isScheduleSelected(selection, schedule) {
    return selection.schedules.has(schedule.scheduleId) || isCoveredByFolderIn(selection.folders, schedule)
}

/**
 * @returns {"all" | "some" | "none"}
 */
export function groupSelectionState(selection, group) {
    const selectedCount = group.schedules.filter(isSelected).length
    if (selectedCount === 0) return "none"
    return selectedCount === group.schedules.length ? "all" : "some"

    function isSelected(schedule) {
        return isScheduleSelected(selection, schedule)
    }
}

/**
 * @param {object} selection
 * @param {object} group
 * @param {{ searching: boolean, groups?: object[] }} options - groups are every listed group,
 *   including any a search hides (the board's allGroups), so unticking can keep schedules
 *   elsewhere that a dropped folder also covered
 */
export function toggleGroup(selection, group, { searching, groups = [group] }) {
    if (groupSelectionState(selection, group) === "all") {
        // Only the schedules on screen: a dropped folder's hidden schedules stay ticked
        if (searching) return deselect(selection, group.schedules, groups)
        const folders = new Set(selection.folders)
        folders.delete(group.folderId)
        return deselect({ folders, schedules: selection.schedules }, group.schedules, groups)
    }
    const folders = new Set(selection.folders)
    const schedules = new Set(selection.schedules)
    const ids = group.schedules.map(toScheduleId)
    if (group.folderId && !searching) {
        folders.add(group.folderId)
        for (const id of ids) schedules.delete(id)
    } else {
        for (const id of ids) schedules.add(id)
    }
    return { folders, schedules }
}

/**
 * Select every visible group (as folders where possible), or clear them all when
 * everything visible is already selected.
 *
 * @param {object} selection
 * @param {object[]} groups - The groups on screen
 * @param {{ searching: boolean, allGroups?: object[] }} options - allGroups are every listed group,
 *   including any a search hides, so clearing while searching keeps the hidden ticks
 */
export function toggleAll(selection, groups, { searching, allGroups = groups }) {
    if (allSelectionState(selection, groups) === "all") {
        if (!searching) return EMPTY_SELECTION
        return deselect(selection, groups.flatMap(toSchedules), allGroups)
    }
    let next = selection
    for (const group of groups) {
        if (groupSelectionState(next, group) !== "all") next = toggleGroup(next, group, { searching, groups })
    }
    return next
}

/**
 * @returns {"all" | "some" | "none"}
 */
export function allSelectionState(selection, groups) {
    const states = new Set(groups.map(stateOf))
    if (states.size === 0 || (states.size === 1 && states.has("none"))) return "none"
    return states.size === 1 && states.has("all") ? "all" : "some"

    function stateOf(group) {
        return groupSelectionState(selection, group)
    }
}

/**
 * @param {object} selection
 * @param {object} group - The group the schedule is listed in
 * @param {string} scheduleId
 * @param {{ groups?: object[] }} [options] - Every listed group, hidden ones too (see toggleGroup)
 */
export function toggleSchedule(selection, group, scheduleId, { groups = [group] } = {}) {
    const schedule = group.schedules.find(hasId) ?? { scheduleId }
    if (isScheduleSelected(selection, schedule)) return deselect(selection, [schedule], groups)
    const schedules = new Set(selection.schedules)
    schedules.add(scheduleId)
    return { folders: new Set(selection.folders), schedules }

    function hasId(candidate) {
        return candidate.scheduleId === scheduleId
    }
}

/**
 * A click on a schedule's tick box. A shift-click selects the range from the last clicked
 * schedule over the rows on screen, which leaves out folders that are shut. If the last
 * clicked schedule is no longer on screen (assigned since, say) the click toggles this
 * one instead of doing nothing, and starts a new range.
 *
 * @param {object} selection
 * @param {{ groups: object[], allGroups?: object[] }} board - Listed groups, and the same before
 *   a search filtered them (see buildAssignmentBoard)
 * @param {{ group: object, scheduleId: string, shiftKey?: boolean, anchorId?: string | null, isGroupOpen: (group: object) => boolean }} click
 */
export function clickSchedule(selection, board, { group, scheduleId, shiftKey, anchorId, isGroupOpen }) {
    const onScreen = board.groups.filter(isGroupOpen).flatMap(toScheduleIds)
    if (shiftKey && onScreen.includes(anchorId)) return selectRange(selection, onScreen, anchorId, scheduleId)
    return toggleSchedule(selection, group, scheduleId, { groups: board.allGroups ?? board.groups })
}

/**
 * A new empty selection. Assigning the ticked work clears the ticks with one of these, so
 * that if the save fails, restoreSelection can tell whether anything was ticked since.
 */
export function clearedSelection() {
    return { folders: new Set(), schedules: new Set() }
}

/**
 * State updater for a failed assign: put back the ticks it cleared, unless the user has
 * ticked or cleared anything since, which is newer than the failed assign.
 *
 * @param {object} previous - The selection before the assign cleared it
 * @param {object} cleared - The selection it cleared it with (see clearedSelection)
 */
export function restoreSelection(previous, cleared) {
    return function restore(current) {
        return current === cleared ? previous : current
    }
}

/**
 * Shift-click: select every schedule between the last clicked one and this one, in the
 * order they are shown.
 *
 * @param {object} selection
 * @param {string[]} orderedIds - Visible schedule ids, top to bottom
 * @param {string} fromId
 * @param {string} toId
 */
export function selectRange(selection, orderedIds, fromId, toId) {
    const from = orderedIds.indexOf(fromId)
    const to = orderedIds.indexOf(toId)
    if (from < 0 || to < 0) return selection
    const schedules = new Set(selection.schedules)
    for (const id of orderedIds.slice(Math.min(from, to), Math.max(from, to) + 1)) schedules.add(id)
    return { folders: new Set(selection.folders), schedules }
}

export function selectionSize(selection, groups) {
    let count = 0
    for (const group of groups) {
        for (const s of group.schedules) if (isScheduleSelected(selection, s)) count++
    }
    return count
}

/**
 * The assignment targets a selection stands for: each selected folder, then any selected
 * schedule no selected folder covers. While searching, only the schedules on screen.
 *
 * @param {object} selection
 * @param {object[]} groups - Visible groups
 * @param {{ searching?: boolean }} [options]
 * @returns {Array<{ kind: string, targetId: string, name: string }>}
 */
export function selectionTargets(selection, groups, { searching = false } = {}) {
    const assignedFolders = new Set(searching ? [] : groups.filter(isSelectedFolder).map(toFolderId))
    const targets = []
    for (const group of groups) {
        if (assignedFolders.has(group.folderId)) targets.push(folderTarget(group))
        for (const s of group.schedules) {
            if (isScheduleSelected(selection, s) && !isCoveredByFolderIn(assignedFolders, s)) {
                targets.push(scheduleTarget(s))
            }
        }
    }
    return targets

    function isSelectedFolder(group) {
        return !!group.folderId && selection.folders.has(group.folderId)
    }
}

/**
 * What a drag carries. With nothing ticked, just the grabbed item. With a selection, the
 * whole selection, plus the grabbed item if the selection does not already cover it (a
 * schedule inside a ticked folder, or a folder with any of its schedules ticked, is covered).
 *
 * @param {object} selection
 * @param {object[]} groups - Visible groups
 * @param {{ group: object, schedule?: object }} grabbed - A folder header, or a schedule row in it
 * @param {{ searching: boolean }} options
 */
export function dragTargets(selection, groups, { group, schedule }, { searching }) {
    const own = schedule ? [scheduleTarget(schedule)] : groupTargets(group, searching)
    const selected = selectionTargets(selection, groups, { searching })
    if (!selected.length) return own
    const covered = schedule
        ? isScheduleSelected(selection, schedule)
        : groupSelectionState(selection, group) !== "none"
    return covered ? selected : [...selected, ...own]
}

// A folder header on its own: the folder itself, or only its visible schedules while searching
function groupTargets(group, searching) {
    return group.folderId && !searching ? [folderTarget(group)] : group.schedules.map(scheduleTarget)
}

export function folderTarget(group) {
    return { kind: "folder", targetId: group.folderId, name: group.label }
}

export function scheduleTarget(schedule) {
    return { kind: "schedule", targetId: schedule.scheduleId, name: `${schedule.code} ${schedule.title}`.trim() }
}

// Untick these schedules. A selected folder that covers any of them is dropped, and the
// other listed schedules it covered stay ticked one by one, so only these ones change.
function deselect(selection, cleared, groups) {
    const clearedIds = new Set(cleared.map(toScheduleId))
    const dropped = new Set(cleared.flatMap(folderIdsOf).filter(isSelectedFolderId))
    const folders = new Set([...selection.folders].filter(isKept))
    const schedules = new Set(selection.schedules)
    for (const group of groups) {
        for (const s of group.schedules) {
            if (!clearedIds.has(s.scheduleId) && isCoveredByFolderIn(dropped, s)) schedules.add(s.scheduleId)
        }
    }
    for (const id of clearedIds) schedules.delete(id)
    return { folders, schedules }

    function isSelectedFolderId(folderId) {
        return selection.folders.has(folderId)
    }

    function isKept(folderId) {
        return !dropped.has(folderId)
    }
}

function isCoveredByFolderIn(folders, schedule) {
    if (!folders.size) return false
    return folderIdsOf(schedule).some(isIn)

    function isIn(folderId) {
        return folders.has(folderId)
    }
}

function folderIdsOf(schedule) {
    return schedule.folderIds || []
}

function toScheduleId(schedule) {
    return schedule.scheduleId
}

function toScheduleIds(group) {
    return group.schedules.map(toScheduleId)
}

function toFolderId(group) {
    return group.folderId
}

function toSchedules(group) {
    return group.schedules
}
